import {
  DiscountType,
  DiscountTypeReference,
  ReceiptDiscountFields,
  ReceiptDiscountType
} from 'src/app/models/discount-type.model';

export const DEFAULT_DISCOUNT_TYPE_ID = 1;

export const DEFAULT_DISCOUNT_TYPES: readonly DiscountType[] = [
  {
    id: 1,
    name: 'AMOUNT',
    description: 'Descuento por litro',
    symbol: '$'
  },
  {
    id: 2,
    name: 'PERCENTAGE',
    description: 'Porcentaje del descuento',
    symbol: '%'
  }
];

interface ReceiptDiscountConfiguration {
  discount?: unknown;
  discount_type_id?: unknown;
  discount_type?: unknown;
  show_discount_on_receipt?: unknown;
}

export function resolveDiscountTypeId(value: unknown): number {
  let rawValue = value;

  if (isRecord(rawValue) && 'data' in rawValue) {
    rawValue = rawValue['data'];
  }

  if (isRecord(rawValue) && 'id' in rawValue) {
    rawValue = rawValue['id'];
  }

  const parsedValue = Number(rawValue);
  return Number.isInteger(parsedValue) && parsedValue > 0
    ? parsedValue
    : DEFAULT_DISCOUNT_TYPE_ID;
}

export function buildDiscountTypeReference(value: unknown): DiscountTypeReference {
  return { id: resolveDiscountTypeId(value) };
}

export function getDiscountTypeOptionLabel(discountType: DiscountType): string {
  return discountType.name.toUpperCase() === 'PERCENTAGE'
    ? 'Descuento en porcentaje'
    : 'Descuento en monto';
}

export function getReceiptDiscountType(value: unknown): ReceiptDiscountType {
  const normalizedValue = resolveDiscountTypeName(value);

  if (normalizedValue === 'PERCENTAGE') {
    return 'percentage';
  }

  if (normalizedValue === 'AMOUNT' || normalizedValue === 'FIXED') {
    return 'fixed';
  }

  return resolveDiscountTypeId(value) === 2 ? 'percentage' : 'fixed';
}

export function formatDiscountValue(value: unknown, type: unknown): string {
  const preformattedDiscount = typeof value === 'string' ? value.trim() : '';
  if (preformattedDiscount.startsWith('$') || preformattedDiscount.endsWith('%')) {
    return preformattedDiscount;
  }

  const discount = resolveReceiptDiscountValue(value);
  if (discount === null) {
    return '-';
  }

  const formattedValue = discount.toFixed(2);
  return getReceiptDiscountType(type) === 'percentage'
    ? `${formattedValue}%`
    : `$${formattedValue}`;
}

export function buildReceiptDiscountFields(
  ...configurations: Array<ReceiptDiscountConfiguration | null | undefined>
): ReceiptDiscountFields | null {
  for (const configuration of configurations) {
    if (!configuration) {
      continue;
    }

    if (!resolveBoolean(configuration.show_discount_on_receipt)) {
      return null;
    }

    const discount = resolveReceiptDiscountValue(configuration?.discount);

    if (discount === null) {
      continue;
    }

    return {
      discount,
      discount_type: getReceiptDiscountType(
        configuration?.discount_type_id ?? configuration?.discount_type
      )
    };
  }

  return null;
}

export function resolveBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === 1 || value === '1' || value === 'true') {
    return true;
  }

  if (value === 0 || value === '0' || value === 'false') {
    return false;
  }

  return fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function resolveDiscountTypeName(value: unknown): string | null {
  let rawValue = value;

  if (isRecord(rawValue) && 'data' in rawValue) {
    rawValue = rawValue['data'];
  }

  if (isRecord(rawValue) && 'name' in rawValue) {
    rawValue = rawValue['name'];
  }

  return typeof rawValue === 'string' ? rawValue.trim().toUpperCase() : null;
}

function resolveReceiptDiscountValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : null;
}
